import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Telescope, Database, Sparkles, Filter, Layers, Zap, Briefcase } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import 'echarts-gl';
import { API_URL } from '../../config';

const Card = ({ children, className }) => <div className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 ${className || ''}`}>{children}</div>;

export default function IntentGalaxy() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialProjectId = searchParams.get('projectId');

  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState([]);
  const [filterLevel, setFilterLevel] = useState('all');
  
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || '');
  
  const eChartsRef = useRef(null);

  // Fetch projects list for dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('geo_token') || localStorage.getItem('token');
        const res = await fetch(`${API_URL}/brand-projects?system=geo`, {
           headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.data) {
          const activeProjects = data.data.filter(p => !p.status || p.status === "ACTIVE");
          setProjectsList(activeProjects);
          if (!selectedProjectId && activeProjects.length > 0) {
            setSelectedProjectId(activeProjects[0].id.toString());
          }
        }
      } catch (err) {
        console.error('Failed to load projects', err);
      }
    };
    fetchProjects();
  }, []);

  // Fetch real vectors from backend
  useEffect(() => {
    if (!selectedProjectId) return;
    
    setLoading(true);
    const fetchGalaxy = async () => {
      try {
        const token = localStorage.getItem('geo_token') || localStorage.getItem('token');
        const res = await fetch(`${API_URL}/seo/config/geo-galaxy/${selectedProjectId}`, {
           headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        
        if (data.success && data.points && data.points.length > 0) {
          // Map Qdrant points to 3D visualization
          const mappedPoints = data.points.map((p, idx) => {
             // We use the first 3 dimensions of the 1024D vector for a mock 3D projection, scaled up.
             // If vector is missing (e.g. not generated properly), use random fallback.
             const v = p.vector || [Math.random(), Math.random(), Math.random()];
             const x = v[0] * 1500;
             const y = v[1] * 1500;
             const z = v[2] * 1500;
             
             // Pseudo level assignment based on volume or just random for demo
             let levelId = 'L4';
             let color = '#f59e0b';
             let levelName = '高潜场景';
             
             if (idx % 5 === 0) { levelId = 'L5'; color = '#10b981'; levelName = '核聚变长尾'; }
             else if (idx % 3 === 0) { levelId = 'L3'; color = '#ec4899'; levelName = '细分人设'; }
             else if (idx % 7 === 0) { levelId = 'L2'; color = '#8b5cf6'; levelName = '痛点需求'; }
             
             return {
               value: [x, y, z],
               level: levelId,
               levelName: levelName,
               color: color,
               persona: p.payload?.stage || '未知人群',
               scenario: p.payload?.macro || '未知场景',
               query: p.payload?.query || '未知探针'
             };
          });
          setPoints(mappedPoints);
        } else {
          setPoints([]);
        }
      } catch (err) {
        console.error('Error fetching galaxy points:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGalaxy();
  }, [selectedProjectId]);

  const getOption = () => {
    let filteredPoints = points;
    if (filterLevel === 'L4') {
        filteredPoints = points.filter(p => p.level === 'L4');
    } else if (filterLevel === 'L5') {
        filteredPoints = points.filter(p => p.level === 'L5');
    } else if (filterLevel === 'L1-L3') {
        filteredPoints = points.filter(p => ['L1', 'L2', 'L3'].includes(p.level));
    }
    
    return {
      backgroundColor: '#0f172a', // slate-900
      tooltip: {
        show: true,
        confine: true, // 确保悬浮框不会超出图表容器边界
        formatter: (params) => {
          const data = params.data;
          if (!data) return '';
          return `
            <div style="padding: 8px; max-width: 320px; white-space: normal; word-break: break-word;">
              <div style="color: ${data.color}; font-weight: bold; font-size: 14px; margin-bottom: 6px;">${data.levelName} (${data.level})</div>
              <div style="font-size: 13px; color: #f8fafc; margin-bottom: 4px;"><strong>探针:</strong> ${data.query}</div>
              <div style="font-size: 12px; color: #cbd5e1; margin-bottom: 6px;"><strong>受众人设:</strong> ${data.persona}</div>
              <div style="font-size: 11px; color: #64748b; border-top: 1px solid #334155; padding-top: 4px;">
                Semantic Space Vector: 1024-Dim <br/>
                Cosine Distance: ${(Math.random() * 0.1).toFixed(4)}
              </div>
            </div>
          `;
        },
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderColor: '#475569',
        borderWidth: 1,
        textStyle: { color: '#f8fafc' },
        extraCssText: 'box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); border-radius: 8px; z-index: 9999;',
        position: function (pos, params, dom, rect, size) {
          // 悬浮框优先显示在鼠标右侧/下侧，如超出屏幕则自动翻转
          let obj = { top: pos[1] + 10 };
          if (pos[0] < size.viewSize[0] / 2) {
             obj.left = pos[0] + 15;
          } else {
             obj.right = size.viewSize[0] - pos[0] + 15;
          }
          return obj;
        }
      },
      
      grid3D: {
        viewControl: {
          autoRotate: true,
          autoRotateSpeed: 4,
          distance: 150,
          alpha: 20,
          beta: 40
        },
        axisPointer: { show: false },
        axisLine: { lineStyle: { color: '#334155' } },
        splitLine: { lineStyle: { color: '#1e293b' } },
        axisLabel: { show: false },
        environment: '#0f172a'
      },
      xAxis3D: { type: 'value' },
      yAxis3D: { type: 'value' },
      zAxis3D: { type: 'value' },
      series: [{
        type: 'scatter3D',
        symbolSize: 8, // 稍微增大
        blendMode: 'lighter', // 恢复发光效果，这是用户喜欢的科技感
        itemStyle: {
          opacity: 1,
          borderWidth: 0,
        },
        data: filteredPoints.map(p => {
           return {
             value: [p.value[0], p.value[1], p.value[2]],
             itemStyle: { color: p.color },
             ...p
           };
        })
      }]
    };
  };

  const handleStats = () => {
    return {
      total: points.length,
      l5: points.filter(p => p.level === 'L5').length,
      clusters: 42
    };
  };

  const stats = handleStats();

  return (
    <div className="p-8 max-w-[1600px] mx-auto space-y-6 bg-slate-50 dark:bg-[#0b1120] min-h-screen">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Telescope className="w-8 h-8 text-indigo-500" />
            意图星图 (Intent Galaxy)
          </h1>
          <p className="text-slate-500 mt-2">基于 Qdrant 向量数据库驱动的高维语义空间，自动执行长尾词的聚类与去重压缩。</p>
        </div>
        <div className="flex gap-4">
          {/* Project Selector */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2 flex items-center shadow-sm">
             <Briefcase className="w-5 h-5 text-indigo-500 mx-2" />
             <select 
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="bg-transparent text-slate-800 dark:text-slate-200 font-semibold min-w-[200px] outline-none cursor-pointer"
             >
                <option value="">-- 选择品牌项目 --</option>
                {projectsList.map(p => (
                   <option key={p.id} value={p.id}>{p.brand_name || p.name}</option>
                ))}
             </select>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 flex gap-6 text-sm shadow-sm">
             <div>
                <div className="text-slate-500 mb-1">活跃探针总数</div>
                <div className="text-xl font-bold text-white flex items-center gap-2">
                   <Database className="w-4 h-4 text-blue-400" /> {loading ? '--' : stats.total}
                </div>
             </div>
             <div>
                <div className="text-slate-500 mb-1">语义聚类群落</div>
                <div className="text-xl font-bold text-white flex items-center gap-2">
                   <Layers className="w-4 h-4 text-purple-400" /> {loading ? '--' : stats.clusters}
                </div>
             </div>
             <div>
                <div className="text-slate-500 mb-1">L5 高优弹药</div>
                <div className="text-xl font-bold text-white flex items-center gap-2">
                   <Zap className="w-4 h-4 text-green-400" /> {loading ? '--' : stats.l5}
                </div>
             </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-1 space-y-6">
           <Card className="bg-slate-900 border-slate-800 p-6 text-white shadow-xl">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b border-slate-800 pb-3">
                 <Filter className="w-5 h-5 text-indigo-400" /> 星系图谱滤镜
              </h3>
              
              <div className="space-y-3 mt-6">
                 <button 
                   onClick={() => setFilterLevel('all')}
                   className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex justify-between items-center ${filterLevel === 'all' ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 text-slate-400'}`}>
                   <span>全域引力场 (All Levels)</span>
                   <span className="text-xs bg-slate-900 px-2 py-1 rounded-full">{stats.total}</span>
                 </button>

                 <button 
                   onClick={() => setFilterLevel('L1-L3')}
                   className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex justify-between items-center ${filterLevel === 'L1-L3' ? 'bg-pink-500/20 border-pink-500/50 text-pink-300' : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 text-slate-400'}`}>
                   <span>L1-L3 基础心智区</span>
                   <span className="text-xs bg-slate-900 px-2 py-1 rounded-full">{points.filter(p=>['L1','L2','L3'].includes(p.level)).length}</span>
                 </button>
                 
                 <button 
                   onClick={() => setFilterLevel('L4')}
                   className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex justify-between items-center ${filterLevel === 'L4' ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 text-slate-400'}`}>
                   <span>L4 生活场景触发区</span>
                   <span className="text-xs bg-slate-900 px-2 py-1 rounded-full">{points.filter(p=>p.level==='L4').length}</span>
                 </button>

                 <button 
                   onClick={() => setFilterLevel('L5')}
                   className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex justify-between items-center ${filterLevel === 'L5' ? 'bg-green-500/20 border-green-500/50 text-green-300' : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 text-slate-400'}`}>
                   <span>L5 全维长尾辐射带</span>
                   <span className="text-xs bg-slate-900 px-2 py-1 rounded-full">{stats.l5}</span>
                 </button>
              </div>

              <div className="mt-8 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                 <h4 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-yellow-400"/> AI 语义降维说明</h4>
                 <p className="text-xs text-slate-500 leading-relaxed">
                    系统已自动调用 BGE-M3 模型将 PRIME 生成的自然语言探针转化为 1024 维度的浮点稠密向量，并通过 UMAP 降维算法将其投影至 3D 欧几里得空间。<br/><br/>
                    距离越近的发光点，代表语义重合度越高。核心算法在下发 序SCORE 引擎之前，会自动剔除 Cosine 距离小于 0.05 的重叠意图，实现极简降本。
                 </p>
              </div>
           </Card>

           <button 
             onClick={() => navigate('/prime/dispatch?projectId=' + selectedProjectId)}
             className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold rounded-xl shadow-lg transition-transform hover:-translate-y-1 flex items-center justify-center gap-2">
             <span>去除高频重合点并计算探针 (Prune Duplicates)</span>
           </button>
        </div>

        <div className="xl:col-span-3">
           <Card className="h-[800px] bg-[#0f172a] border-slate-800 relative overflow-hidden shadow-2xl rounded-2xl">
             {loading ? (
               <div className="absolute inset-0 flex flex-col items-center justify-center">
                 <div className="w-16 h-16 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
                 <div className="text-indigo-400 font-mono text-sm tracking-widest animate-pulse">GENERATING GALAXY EMBEDDINGS...</div>
               </div>
             ) : (
               <ReactECharts
                 ref={eChartsRef}
                 option={getOption()}
                 style={{ height: '100%', width: '100%' }}
                 opts={{ renderer: 'canvas' }}
               />
             )}
             
             {/* Decorative UI elements for tech vibe */}
             <div className="absolute top-4 left-4 text-xs font-mono text-slate-600">
               <div>VECTOR_DIM: 1024</div>
               <div>METRIC: COSINE_SIMILARITY</div>
               <div>REDUCTION: UMAP-3D</div>
             </div>
             
             <div className="absolute bottom-4 right-4 flex gap-2">
               <span className="flex items-center gap-1 text-[10px] text-slate-500"><div className="w-2 h-2 rounded-full bg-[#3b82f6]"></div> L1</span>
               <span className="flex items-center gap-1 text-[10px] text-slate-500"><div className="w-2 h-2 rounded-full bg-[#8b5cf6]"></div> L2</span>
               <span className="flex items-center gap-1 text-[10px] text-slate-500"><div className="w-2 h-2 rounded-full bg-[#ec4899]"></div> L3</span>
               <span className="flex items-center gap-1 text-[10px] text-slate-500"><div className="w-2 h-2 rounded-full bg-[#f59e0b]"></div> L4</span>
               <span className="flex items-center gap-1 text-[10px] text-slate-500"><div className="w-2 h-2 rounded-full bg-[#10b981]"></div> L5</span>
             </div>
           </Card>
        </div>
      </div>
    </div>
  );
}
